// components
import {
  FieldSelectedSections,
  LocalizationProvider
} from '@mui/x-date-pickers-pro';

// hooks
import { useRef, useState } from 'react';

// styles
import { StyledDatePicker } from './ControlledDatePicker.styles';

// types
import { ControlledDatePickerProps } from './ControlledDatePicker.types';

// utils
import { Controller } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import { customDatePickerLocaleText } from './ControlledDatePicker.locale';

export default function ControlledDatePicker({
  control,
  name,
  label,
  disabled,
  size = 'small',
  variant = 'outlined',
  onChange,
  shouldDisableDate,
  fullWidth,
  minDate,
  maxDate,
  required,
  showTodayButton,
  bypassRef = false
}: ControlledDatePickerProps) {
  const [selectedSection, setSelectedSection] =
    useState<FieldSelectedSections>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedFieldOnChange = debounce(
    (field, newDate: Date | Dayjs | null) => {
      if (newDate && dayjs(newDate, 'DD-MM-YYYY', true).isValid()) {
        field.onChange(newDate);
        onChange?.(newDate);
      }
    }
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="nl"
      localeText={customDatePickerLocaleText}
    >
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          return (
            <StyledDatePicker
              {...field}
              value={
                field.value && dayjs(field.value).isValid()
                  ? dayjs(field.value)
                  : null
              }
              views={['year', 'month', 'day']}
              ref={bypassRef ? undefined : control.register(name)?.ref}
              disabled={disabled}
              sx={{
                width: fullWidth ? '100%' : '160px'
              }}
              selectedSections={selectedSection}
              onSelectedSectionsChange={setSelectedSection}
              slotProps={{
                field: {
                  clearable: true
                },
                textField: {
                  inputRef,
                  required,
                  variant,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                  size,
                  onKeyDownCapture: (e) => {
                    if (
                      (e.code === 'Delete' || e.code === 'Backspace') &&
                      selectedSection === 'all'
                    ) {
                      field.onChange(null);
                    }
                  }
                },
                clearButton: {
                  onClickCapture: () => {
                    field.onChange(null);
                  }
                },
                ...(showTodayButton && {
                  actionBar: {
                    actions: ['today']
                  }
                })
              }}
              label={label}
              shouldDisableDate={shouldDisableDate}
              onChange={(newDate) => {
                const isValidDate = dayjs(newDate).isValid();
                const isValidYear =
                  isValidDate && dayjs(newDate).year().toString().length === 4;

                if (!isValidDate && !isValidYear) return;

                debouncedFieldOnChange(field, newDate);
              }}
              minDate={
                dayjs(minDate).isValid()
                  ? dayjs(minDate, 'YYYY-MM-DD')
                  : undefined
              }
              maxDate={
                dayjs(maxDate).isValid()
                  ? dayjs(maxDate, 'YYYY-MM-DD')
                  : undefined
              }
            />
          );
        }}
      />
    </LocalizationProvider>
  );
}
